import React, { useState, useEffect } from 'react'
import { Shield, Heart, Users, Award, ChevronDown, ChevronUp, Key, FileText, CreditCard, User, HelpCircle } from 'lucide-react'
import { trackAboutPageView } from '../../utils/eventTracking'

const About = () => {
  const [expandedSupport, setExpandedSupport] = useState(null)

  // Track page view on mount
  useEffect(() => {
    trackAboutPageView()
  }, [])

  const toggleSupport = (index) => {
    setExpandedSupport(expandedSupport === index ? null : index)
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">About YourInsurance</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Your trusted partner for comprehensive insurance solutions, 
              providing health and life insurance coverage for over a decade.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              To provide accessible, affordable, and comprehensive insurance solutions 
              that protect what matters most to you and your family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Protection</h3>
              <p className="text-gray-600">Comprehensive coverage for your health and life</p>
            </div>

            <div className="text-center">
              <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Heart className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Care</h3>
              <p className="text-gray-600">Dedicated support when you need it most</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600">Serving millions of satisfied customers</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Award className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Excellence</h3>
              <p className="text-gray-600">Award-winning service and reliability</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Impact</h2>
            <p className="text-xl text-gray-600">
              Numbers that speak to our commitment and success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">50K+</div>
              <div className="text-lg text-gray-600">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">99%</div>
              <div className="text-lg text-gray-600">Claim Settlement Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">500+</div>
              <div className="text-lg text-gray-600">Network Hospitals</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">10+</div>
              <div className="text-lg text-gray-600">Years of Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Let's help you find the right support</h2>
            <p className="text-xl text-gray-600">
              Get the help you need with our comprehensive support resources
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a href="#account" className="text-blue-600 hover:text-blue-800 underline">Account registration</a>
            <a href="#receipts" className="text-blue-600 hover:text-blue-800 underline">Policy receipts & tax slips</a>
            <a href="#forms" className="text-blue-600 hover:text-blue-800 underline">Claim forms</a>
            <a href="#coverage" className="text-blue-600 hover:text-blue-800 underline">Coverage card</a>
          </div>

          {/* Support Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Signing In */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => toggleSupport(0)}>
              <div className="flex items-center justify-between mb-4">
                <Key className="h-8 w-8 text-blue-600" />
                {expandedSupport === 0 ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Signing in</h3>
              <p className="text-gray-600 mb-4">Learn how to reset your password, recover your Access ID, and get other answers.</p>
              {expandedSupport === 0 && (
                <div className="mt-4 space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Password Reset</h4>
                    <p className="text-gray-600 text-sm">Click "Forgot Password" on login page and follow the email instructions.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Access ID Recovery</h4>
                    <p className="text-gray-600 text-sm">Contact customer support with your policy number and personal details.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Two-Factor Authentication</h4>
                    <p className="text-gray-600 text-sm">Set up 2FA for enhanced security in your account settings.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Submitting Claims */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => toggleSupport(1)}>
              <div className="flex items-center justify-between mb-4">
                <FileText className="h-8 w-8 text-green-600" />
                {expandedSupport === 1 ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Submitting claims</h3>
              <p className="text-gray-600 mb-4">Get instructions on how to submit and track a benefits or insurance claim.</p>
              {expandedSupport === 1 && (
                <div className="mt-4 space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Online Claim Submission</h4>
                    <p className="text-gray-600 text-sm">Log into your account, go to Claims section, and upload required documents.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Cashless Claims</h4>
                    <p className="text-gray-600 text-sm">Visit network hospitals and present your policy card for direct settlement.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Claim Tracking</h4>
                    <p className="text-gray-600 text-sm">Track your claim status in real-time through our mobile app or website.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Checking Coverage */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => toggleSupport(2)}>
              <div className="flex items-center justify-between mb-4">
                <Shield className="h-8 w-8 text-purple-600" />
                {expandedSupport === 2 ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Checking coverage</h3>
              <p className="text-gray-600 mb-4">Learn how to get your coverage card and see what you're covered for in your plan.</p>
              {expandedSupport === 2 && (
                <div className="mt-4 space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Digital Coverage Card</h4>
                    <p className="text-gray-600 text-sm">Download your coverage card from the mobile app or customer portal.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Coverage Details</h4>
                    <p className="text-gray-600 text-sm">View your policy benefits, exclusions, and coverage limits online.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Network Hospitals</h4>
                    <p className="text-gray-600 text-sm">Find nearby network hospitals and check if they accept your policy.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Managing Policy */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => toggleSupport(3)}>
              <div className="flex items-center justify-between mb-4">
                <CreditCard className="h-8 w-8 text-orange-600" />
                {expandedSupport === 3 ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Managing policy</h3>
              <p className="text-gray-600 mb-4">Get instructions for updating your policy, changing beneficiaries, and more.</p>
              {expandedSupport === 3 && (
                <div className="mt-4 space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Policy Updates</h4>
                    <p className="text-gray-600 text-sm">Update your personal information, contact details, and beneficiaries online.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Premium Payments</h4>
                    <p className="text-gray-600 text-sm">Set up auto-pay, view payment history, and update payment methods.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Policy Renewal</h4>
                    <p className="text-gray-600 text-sm">Renew your policy online and explore additional coverage options.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Updating Information */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => toggleSupport(4)}>
              <div className="flex items-center justify-between mb-4">
                <User className="h-8 w-8 text-indigo-600" />
                {expandedSupport === 4 ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Updating information</h3>
              <p className="text-gray-600 mb-4">Learn how to update your banking details, home address, or employer information.</p>
              {expandedSupport === 4 && (
                <div className="mt-4 space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Personal Details</h4>
                    <p className="text-gray-600 text-sm">Update your name, address, phone number, and email address.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Banking Information</h4>
                    <p className="text-gray-600 text-sm">Change your bank account details for premium payments and claim settlements.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Employment Details</h4>
                    <p className="text-gray-600 text-sm">Update your employer information and job title for group policies.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Finding Documents */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => toggleSupport(5)}>
              <div className="flex items-center justify-between mb-4">
                <HelpCircle className="h-8 w-8 text-red-600" />
                {expandedSupport === 5 ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Finding documents</h3>
              <p className="text-gray-600 mb-4">Learn how to find documents like policy certificates, tax slips, and claim forms.</p>
              {expandedSupport === 5 && (
                <div className="mt-4 space-y-3">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Policy Documents</h4>
                    <p className="text-gray-600 text-sm">Download your policy certificate, schedule, and terms & conditions.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Tax Documents</h4>
                    <p className="text-gray-600 text-sm">Access your premium receipts and tax certificates for filing returns.</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-semibold text-gray-900">Claim Forms</h4>
                    <p className="text-gray-600 text-sm">Download claim forms, medical certificates, and other required documents.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
